import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Color, CommonStyles } from '../../styles';
import { getCurrenciesList, getExpenseCategories } from '../../store/actions/expense';
import {
  selectExpenseCategories,
  selectExpenseList,
  selectCurrencies,
  selectCurrentReportIdForUpdate,
  selectExpenseStates,
} from '../../store/selectors/expense.selector';
import { filterReportsForNewExpense } from '../../utils/common.utils';
import {
  expenseInputTypes,
  EXPENSE_FORM_KEYS,
  EXPENSE_ACTION_TYPES,
  EXPENSE_SUB_TAB_OPTIONS,
} from '../../constants/Enums';
import ReportsToggle from './ReportsToggle';
import {
  DynamoDBFieldNames,
  LAYOUT,
  REPORT_TOGGLE_KEY,
  REPORT_TOGGLE_OPTIONS,
} from '../../constants/ExpenseCategories';
import ExpenseCategorySelector from './ExpenseCategorySelector';
import ExpenseDynamicFields, { DynamicField } from './ExpenseDynamicFields';
import { verticalBase } from '../../styles/CommonProperties';
import MileageCalculationSelector from './MileageCalculationSelector';

const ExpenseFormFields = ({
  style,
  isCreationForm = true,
  subTab,
  isEditable = true,
  step,
  lockedFields = [],
  fromExistingReport = false,
  isMileage = false,
}) => {
  const reduxDispatch = useDispatch();
  const expenseCategories = useSelector(selectExpenseCategories);
  const expenseList = useSelector(selectExpenseList);
  const currencies = useSelector(selectCurrencies);
  const reportFromRedux = useSelector(selectCurrentReportIdForUpdate);
  const [reportNames, setReportNames] = useState([]);
  const actionType = isCreationForm ? EXPENSE_ACTION_TYPES.CREATION : EXPENSE_ACTION_TYPES.UPDATE;
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const currentSelectedSubCategory = expenseStates[DynamoDBFieldNames.SELECTED_SUBCATEGORY];

  const reportsInput = {
    inputConfig: {
      inputType: expenseInputTypes.PULL_DOWN,
      options: reportNames,
    },
    placeholder: 'Report',
    // stateName for report is the id for the dropdown only, not being used in redux
    stateName: 'report',
    icon: null,
    // mandatory fields not getting used, but needed show or not the optional label
    mandatory: {
      [EXPENSE_ACTION_TYPES.CREATION]: true,
      [EXPENSE_ACTION_TYPES.UPDATE]: true,
    },
    layoutConfig: {
      span: LAYOUT.FULL_WIDTH,
    },
  };

  useEffect(() => {
    const filteredReportsNames = filterReportsForNewExpense(expenseList);
    setReportNames(filteredReportsNames);
  }, [expenseList]);

  useEffect(() => {
    if (!currencies) {
      reduxDispatch(getCurrenciesList());
    }
  }, [currencies, reduxDispatch]);

  useEffect(() => {
    if (!expenseCategories) {
      reduxDispatch(getExpenseCategories());
    }
  }, [reduxDispatch, expenseCategories]);

  const shouldDisplayReportsToggle = () => {
    if (subTab === EXPENSE_SUB_TAB_OPTIONS.MILEAGE || subTab === EXPENSE_SUB_TAB_OPTIONS.MANUAL) {
      return step === EXPENSE_FORM_KEYS.STEP_ONE && isCreationForm && !fromExistingReport;
    }
    return false;
  };

  return (
    <>
      <View
        style={[
          step === EXPENSE_FORM_KEYS.STEP_ONE
            ? CommonStyles.stepOneExpenseCard
            : { ...CommonStyles.stepTwoExpenseCard },
          style,
        ]}
      >
        {isMileage ? (
          <MileageCalculationSelector
            isEditable={isEditable}
            actionType={actionType}
            subTab={subTab}
          />
        ) : (
          step === EXPENSE_FORM_KEYS.STEP_ONE && (
            <ExpenseCategorySelector
              isEditable={isEditable}
              currentSelectedSubCategory={currentSelectedSubCategory}
              actionType={actionType}
              subTab={subTab}
            />
          )
        )}
        <ExpenseDynamicFields
          step={step}
          actionType={actionType}
          subTab={subTab}
          isEditable={isEditable}
          lockedFields={lockedFields}
        />
        {step === EXPENSE_FORM_KEYS.STEP_TWO &&
          expenseStates[REPORT_TOGGLE_KEY] === REPORT_TOGGLE_OPTIONS.EXISTING.label &&
          isCreationForm &&
          !fromExistingReport && (
            <DynamicField
              input={reportsInput}
              isEditable={isEditable}
              actionType={actionType}
              subTab={subTab}
              value={reportFromRedux?.Name}
              style={styles.reportsDropdown}
            />
          )}
      </View>
      {shouldDisplayReportsToggle() && (
        <View style={styles.reportsMargin}>
          <ReportsToggle subTab={subTab} isDisabled={reportNames?.length === 0} />
        </View>
      )}
    </>
  );
};

export const styles = StyleSheet.create({
  reportsMargin: {
    marginTop: verticalBase,
  },
  reportsDropdown: {
    borderTopColor: Color.formDividerColor,
    borderTopWidth: 0.4,
  },
});

export default ExpenseFormFields;
